import { createMuiTheme } from '@material-ui/core/styles';
import colors from 'colors';

declare module '@material-ui/core/styles/overrides' {
    export interface ComponentNameToClassKey {
        MuiAutocomplete: {
            inputRoot: {
                height: number;
            };
        };
    }
}

const theme = createMuiTheme({
    typography: {
        fontFamily: ['Poppins', 'serif'].join(','),
    },
    palette: {
        primary: {
            // light: will be calculated from palette.primary.main,
            main: colors.blue,
            // dark: will be calculated from palette.primary.main,
            contrastText: colors.white,
        },
        secondary: {
            main: colors.green,
            // dark: will be calculated from palette.secondary.main,
            contrastText: colors.white,
        },
        // Used by `getContrastText()` to maximize the contrast between
        // the background and the text.
        contrastThreshold: 3,
        // Used by the functions below to shift a color's luminance by approximately
        // two indexes within its tonal palette.
        // E.g., shift from Red 500 to Red 300 or Red 700.
        tonalOffset: 0.2,
        error: {
            main: colors.red,
        },
        text: {
            primary: colors.dark,
        },
    },
    props: {
        MuiButtonBase: {
            disableRipple: true,
        },
    },
    overrides: {
        MuiButton: {
            root: {
                fontSize: 16,
                lineHeight: '24px',
                color: colors.darkGrey,
                textTransform: 'none',
                '&:hover': { backgroundColor: 'transparent' },
            },
            contained: {
                boxShadow: 'none',
            },
            label: {
                padding: 0,
            },
            textSizeSmall: {
                padding: 0,
                fontSize: 14,
            },
            text: {
                padding: 0,
                minWidth: 'initial',
                fontWeight: 500,
                '&:hover': {
                    color: colors.mediumGrey,
                    backgroundColor: 'transparent',
                },
            },
            textPrimary: {
                color: colors.blue,
                '&:hover': {
                    color: colors.mediumBlue,
                    backgroundColor: 'transparent',
                },
            },
            textSecondary: {
                color: colors.red,
                '&:hover': {
                    color: colors.lightRed,
                    backgroundColor: 'transparent',
                },
            },
            outlinedSizeSmall: {
                padding: '12px 24px',
                fontSize: 14,
                lineHeight: '22px',
            },
            outlinedPrimary: {
                height: 56,
                padding: '16px 32px',
                '&:hover': { backgroundColor: colors.whiteBlue },
            },
            outlinedSecondary: {
                backgroundColor: colors.lightGrey,
                color: colors.dark,
                border: `1px solid ${colors.coolGrey}`,
                padding: '16px 32px',
                '&:hover': {
                    backgroundColor: colors.lighterGrey,
                    border: `1px solid ${colors.coolGrey}`,
                },
                '&$disabled': {
                    background: colors.grey,
                    border: `1px solid ${colors.grey}`,
                    color: colors.white,
                },
            },
            containedPrimary: {
                height: 56,
                padding: '16px 32px',
                '&:hover': { backgroundColor: colors.mediumBlue, boxShadow: 'none' },
                '&$disabled': {
                    backgroundColor: colors.grey,
                    color: colors.white,
                },
            },
            containedSecondary: {
                height: 56,
                padding: '16px 32px',
                backgroundColor: colors.white,
                border: `1px solid ${colors.blue}`,
                color: colors.blue,
                '&:hover': { backgroundColor: colors.whiteBlue, boxShadow: 'none' },
                '&$disabled': {
                    backgroundColor: colors.grey,
                    color: colors.white,
                    borderColor: colors.grey,
                },
            },
        },
        MuiIconButton: {
            root: {
                '&:hover': {
                    backgroundColor: 'transparent',
                },
            },
        },
        MuiMenu: {
            paper: {
                background: colors.dark,
                color: colors.white,
            },
        },
        MuiPaper: {
            elevation1: {
                boxShadow: '2px 2px 8px #E6EAF2',
            },
        },
        MuiExpansionPanel: {
            rounded: {
                borderRadius: 4,
            },
            root: {
                background: `${colors.white} !important`,
            },
        },
        MuiTableCell: {
            head: {
                paddingTop: 0,
                color: colors.darkGrey,
            },
        },
        MuiInputBase: {
            root: {
                height: 48,
            },
            input: {
                '&::placeholder': {
                    color: colors.coolGrey,
                    opacity: 1,
                },
            },
        },
        MuiFormHelperText: {
            root: {
                marginLeft: 0,
                marginRight: 0,
                marginTop: 4,
                fontSize: 12,
                lineHeight: '18px',
            },
            contained: {
                marginLeft: 0,
                marginRight: 0,
                marginTop: 4,
                fontSize: 12,
                lineHeight: '18px',
            },
        },
        MuiAutocomplete: {
            inputRoot: {
                height: 'auto',
                minHeight: 56,
            },
        },
        MuiTypography: {
            h1: {
                fontSize: 24,
                lineHeight: '36px',
                fontWeight: 500,
            },
            h2: {
                fontSize: 20,
                lineHeight: '30px',
                fontWeight: 500,
            },
            body1: {
                fontSize: 16,
                lineHeight: '24px',
                fontWeight: 'normal',
            },
        },
        MuiInputLabel: {
            root: {
                fontSize: 14,
                lineHeight: '21px',
                color: colors.dark,
                marginBottom: 8, // 4px for line input
            },
        },
        MuiSelect: {
            select: {
                '&:focus': {
                    backgroundColor: 'transparent',
                },
            },
        },
        MuiDialogActions: {
            spacing: {
                padding: '16px 24px',
                '&> :not(:first-child)': {
                    marginLeft: '16px',
                },
            },
        },
        MuiDialog: {
            paperWidthMd: {
                maxWidth: '704px',
            },
        },
    },
});

export default theme;
